import { Download, ArrowRight } from 'lucide-react';

const PaymentRoutesTable = ({ payments, onDownload }) => {
  if (!payments || payments.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-12 text-center">
        <p className="text-slate-500 text-sm">Upload SWIFT XML messages to begin route analysis</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-slate-500">Payment Routes Summary</h3>
        <span className="text-xs text-slate-600">{payments.length} payment(s) analyzed</span>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full text-xs min-w-[1200px]">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 font-semibold text-slate-600">#</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-600">Corridor</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-600">Currency</th>
              <th className="text-right px-4 py-3 font-semibold text-slate-600">Amount</th>
              <th className="text-left px-4 py-3 font-semibold text-slate-600">Optimal CB</th>
              <th className="text-right px-4 py-3 font-semibold text-slate-600">Cost</th>
              <th className="text-right px-4 py-3 font-semibold text-slate-600">Time</th>
              <th className="text-right px-4 py-3 font-semibold text-slate-600">SLA</th>
              <th className="text-center px-4 py-3 font-semibold text-slate-600">Route</th>
              <th className="text-center px-4 py-3 font-semibold text-slate-600">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {payments.map((payment, idx) => {
              const hasOptimal = payment.optimalRoute !== null;
              const route = payment.optimalRoute;
              const uniqueKey = `${payment.payment.Corridor}-${payment.payment.Currency}-${payment.payment.Amount}-${idx}`;
              
              return (
                <tr 
                  key={uniqueKey} 
                  className={`hover:bg-slate-50 ${hasOptimal ? 'bg-emerald-50' : ''}`}
                  data-testid={`payment-row-${idx}`}
                >
                  <td className="px-4 py-3 font-mono text-slate-700">{idx + 1}</td>
                  <td className="px-4 py-3">
                    <span className="font-medium text-slate-900">{payment.payment.Corridor}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-slate-700">{payment.payment.Currency}</span>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700">
                    ${(payment.payment.Amount || 0).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    {hasOptimal ? (
                      <span className="font-mono font-bold text-emerald-700">{route.cb_bic}</span>
                    ) : (
                      <span className="text-slate-400 text-xs">Pending...</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {hasOptimal ? (
                      <span className="font-mono text-slate-700">${route.cost}</span>
                    ) : (
                      <span className="text-slate-300">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {hasOptimal ? (
                      <span className="font-mono text-slate-700">{route.avg_time_hours}h</span>
                    ) : (
                      <span className="text-slate-300">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {hasOptimal ? (
                      <span className="font-mono text-slate-700">{(route.sla_score * 100).toFixed(0)}%</span>
                    ) : (
                      <span className="text-slate-300">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {hasOptimal && (
                      <div className="flex items-center justify-center gap-1 text-xs text-slate-600">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">S</span>
                        <ArrowRight className="w-3 h-3" />
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded">
                          {route.intermediaries} Int
                        </span>
                        <ArrowRight className="w-3 h-3" />
                        <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded font-semibold">CB</span>
                        <ArrowRight className="w-3 h-3" />
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded">R</span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {hasOptimal && (
                      <button
                        onClick={() => onDownload(idx)}
                        className="text-xs bg-sky-600 text-white px-3 py-1.5 rounded hover:bg-sky-700 transition-colors inline-flex items-center gap-1"
                        data-testid={`download-btn-${idx}`}
                      >
                        <Download className="w-3 h-3" />
                        XML
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {payments.some(p => p.optimalRoute) && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <p className="text-xs text-emerald-800">
            <strong>✓ Optimization Complete:</strong> {payments.filter(p => p.optimalRoute).length} of {payments.length} payment(s) have optimal routes assigned. Click "XML" to download modified SWIFT messages.
          </p>
        </div>
      )}
    </div>
  );
};

export default PaymentRoutesTable;
